'use client'
import { useMediaQuery } from '@mantine/hooks'
import CourseInfo, { IApplyPeriodList } from '../../components/CourseInfo'
import HTMLRender from '../../components/DetailRender/HTMLRender'
import FAQAccordion from '../../components/FAQAccordion'
import dayjs from 'dayjs'
import isSameOfAfter from 'dayjs/plugin/isSameOrAfter'
import CourseHero from '../../components/CourseHero'
import KDTCurriculum from '../../components/KDTCurriculum'
import { useKDTAttr } from '../../hooks/useKDTAttr'
import { useCurSchoolMyKDTApply } from '../../queries/useCurSchoolMyKDTApply'
import { useMyKDTApplyList } from '../../queries/useMyKDTApplyList'
import { ICurSchoolMyKDTApply } from '../../types'
import checkMySimplifyKdtPeriod from '../../utils/checkMySimplifyKdtPeriod'
import KlassDetailNav from '../../components/KlassDetailNav'
import { useKlassReviewQuery } from '../../queries/useKlassReviewQuery'
import _ from 'lodash'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { IKDTApplyStatus } from '../../types'
import { useAuth } from '@/shared/providers/Auth'
import { useCourseFaqList } from '../../hooks/useCourseFaqList'
import { AppThunkDispatch, RootState, wrapper } from 'stores'
import { MyKDTApplyStatus } from 'stores/apply'
import { fetchKDTKlassAttrThunk, ICourse, IKDTKlass } from 'stores/courses'
import { axiosErrorHandler } from '@/shared/utils/axiosErrorHandler'

import FullPublishingDetail from './FullPublishingDetail'
import FullReviewBySchool from './FullReviewBySchool'
import styles from './klassDetail.module.scss'
import { useViewItem } from '@/shared/gtm/hooks/useViewItem'
import PreNoticeDrawer from 'app/pre-notice/_components/PreNoticeDrawer'
import { FormProvider, useForm } from 'react-hook-form'
import PreNoticeForm from 'app/pre-notice/_components/PreNoticeForm'
import useApplyPreNotice from 'app/pre-notice/_queries/useApplyPreNotice'
import { IApplyPreNotice } from 'app/pre-notice/_types'
import PreNoticeFormModal from 'app/pre-notice/_components/PreNoticeFormModal'
import { usePreNoticeInfoQuery } from 'app/pre-notice/_queries/usePreNoticeInfoQuery'
import SchoolHead from 'app/school/[id]/head'
dayjs.extend(isSameOfAfter)

export const getServerSideProps = wrapper.getServerSideProps(async ({ store }) => {
  const dispatch = store.dispatch as AppThunkDispatch
  const { router } = store.getState() as RootState
  const schoolId = router.location.pathname.split('/')[2]
  if (schoolId) {
    try {
      await dispatch(fetchKDTKlassAttrThunk(schoolId))
    } catch (e) {
      axiosErrorHandler(e)
    }
  }
})

interface IKlassDetailProps {
  schoolId: string
}

const KlassDetail = ({ schoolId }: IKlassDetailProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOpenPreNotice, setIsOpenPreNotice] = useState(false)
  const router = useRouter()
  const { isSigned, profile } = useAuth()
  const { kdtAttr: kdtKlass, applyStatus } = useKDTAttr(schoolId)
  const { data: fetchCurSchoolMyKDTApply, isLoading } = useCurSchoolMyKDTApply(
    schoolId,
    applyStatus,
  )
  const { data: reviewData } = useKlassReviewQuery(schoolId)
  const { data: preNoticeInfo } = usePreNoticeInfoQuery()

  const klass = _.omit({ ...kdtKlass }, ['kdtAttr']) as ICourse
  const isFront = schoolId.includes('kdt-frontend-')
  const preNoticeCourseIds = useMemo(() => {
    return schoolId.split('-')[1]
  }, [schoolId])

  const isHiddenNav = useMemo(() => isFront, [schoolId])
  const applyUrl = useMemo(() => {
    if (kdtKlass?.apiVersion === 'v3') {
      return `/school/${schoolId}/kdt-apply`
    } else {
      return `/school/${schoolId}/apply`
    }
  }, [kdtKlass])

  useViewItem(klass, schoolId)

  const { courseFAQList } = useCourseFaqList(schoolId)
  const { data: myKDTApplyList, isLoading: myKDTApplyListLoading } = useMyKDTApplyList(isSigned)
  const isSimplifyKDT = useMemo(() => kdtKlass?.flags?.includes('new-kdt'), [kdtKlass])
  const isKDT = useMemo(
    () =>
      klass.courseType === 'kdt' ||
      klass.courseType === 'kdt_plus' ||
      klass.courseType === 'kdt_startup',
    [klass],
  )
  const isFullPublishing = useMemo(() => {
    if (kdtKlass) {
      return !kdtKlass.flags.includes('editor')
    }
  }, [kdtKlass])

  const simplifyApplyStatus = checkMySimplifyKdtPeriod({
    startedAt: kdtKlass?.startedAt as string,
    assignmentStartedAt: kdtKlass?.kdtAttr?.assignmentStartedAt as string,
    assignmentEndedAt: kdtKlass?.kdtAttr?.assignmentEndedAt as string,
    participationStartedAt: kdtKlass?.kdtAttr?.participationStartedAt as string,
    participationEndedAt: kdtKlass?.kdtAttr?.participationEndedAt as string,
    endedAt: kdtKlass?.endedAt as string,
  })

  const [hasReview, setHasReview] = useState<boolean>()

  const isMobile = useMediaQuery('(max-width: 1023px)')

  const applyPeriodList: IApplyPeriodList = useMemo(
    () => ({
      early: {
        startedAt: kdtKlass?.kdtAttr?.assignmentStartedAt as string,
        endedAt: kdtKlass?.kdtAttr?.assignmentEndedAt as string,
      },
      apply: {
        startedAt: kdtKlass?.applyStartedAt as string,
        endedAt: kdtKlass?.applyEndedAt as string,
      },
      assignment: {
        startedAt: kdtKlass?.kdtAttr?.assignmentStartedAt as string,
        endedAt: kdtKlass?.kdtAttr?.assignmentEndedAt as string,
      },
      assignmentReview: {
        startedAt: kdtKlass?.kdtAttr?.assignmentEndedAt as string,
        endedAt: kdtKlass?.kdtAttr?.participationStartedAt as string,
      },
      participation: {
        startedAt: kdtKlass?.kdtAttr?.participationStartedAt as string,
        endedAt: kdtKlass?.kdtAttr?.participationEndedAt as string,
      },
      participationReview: {
        startedAt: kdtKlass?.kdtAttr?.participationStartedAt as string,
        endedAt: kdtKlass?.kdtAttr?.participationEndedAt as string,
      },
      ing: {
        startedAt: kdtKlass?.kdtAttr?.participationStartedAt as string,
        endedAt: kdtKlass?.kdtAttr?.participationEndedAt as string,
      },
    }),
    [kdtKlass],
  )

  const curStatus: IKDTApplyStatus = {
    apply: 'apply',
    assignment: 'assignment',
    assignmentReview: 'assignmentReview',
    participation: dayjs().isBefore(kdtKlass?.startedAt) ? 'participation' : 'ing',
    participationReview: dayjs().isBefore(kdtKlass?.startedAt) ? 'participationReview' : 'ing',
    ing: 'ing',
    end: 'end',
  }

  const expiryTimestamp = useMemo(
    () => (applyStatus ? new Date(applyPeriodList[applyStatus]?.endedAt) : new Date()),
    [applyStatus, kdtKlass],
  )

  const checkPrevStepApplicationPassed = (data: MyKDTApplyStatus[] | undefined, type: string) =>
    _.chain(data).filter(['applyType', type]).head().get('status').isEqual('passed').value()

  const isCurCourse = myKDTApplyList && _.isEqual(myKDTApplyList[0]?.id, schoolId)
  const isEditor = _.includes(kdtKlass?.flags, 'editor')

  const { mutate: applyPreNotice } = useApplyPreNotice()

  const methods = useForm<IApplyPreNotice>({
    defaultValues: {
      courseIds: [],
      phoneNo: profile?.phoneNo,
      email: profile?.email,
      reason: '',
    },
  })

  const onSubmit = (data: IApplyPreNotice) => {
    applyPreNotice(
      { ...data, createdAt: dayjs().format() },
      {
        onSuccess: () => {
          setIsOpenPreNotice(false)
          methods.reset()
        },
      },
    )
  }

  const getSubmitType = (
    applyStatus: string,
    curSchoolMyKDTApply: ICurSchoolMyKDTApply | undefined,
    kdtKlass: IKDTKlass | null,
  ): { [key: string]: { disabled: boolean; name: string; period?: string; handler: any } } => {
    const today = dayjs()
    const prevStepPassedUser = checkPrevStepApplicationPassed(
      myKDTApplyList?.filter(({ id }) => id === schoolId)[0]?.applies,
      prevStatus[applyStatus],
    )

    // kdt 간소화
    if (isSimplifyKDT) {
      // 1차 시작 전(사전알림기간)
      if (today.isBefore(kdtKlass?.kdtAttr?.assignmentStartedAt)) {
        return {
          early: {
            disabled: false,
            name: '사전알림 신청',
            handler: () => setIsOpenPreNotice(true),
          },
        }
      }
      // mykdtId !== schoolId 인 경우
      if (!isCurCourse) {
        if (applyStatus === 'assignment') {
          if (today.isBefore(kdtKlass?.kdtAttr?.assignmentEndedAt)) {
            return {
              assignment: {
                disabled: false,
                name: '지원하기',
                period: 'applying',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            return {
              assignment: {
                disabled: true,
                name: '지원 마감',
                handler: () => null,
              },
            }
          }
        } else {
          return {
            [curStatus[applyStatus]]: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        }
      }
      // 지원 시작(과제 = 1차)
      else if (today.isBefore(kdtKlass?.kdtAttr?.assignmentEndedAt)) {
        if (!curSchoolMyKDTApply?.isFinished) {
          return {
            assignment: {
              disabled: false,
              name: '지원하기',
              period: 'applying',
              handler: () => router.push(applyUrl),
            },
          }
        } else {
          return {
            assignment: {
              disabled: true,
              name: '지원완료',
              handler: () => null,
            },
          }
        }
        // 지원 심사
      } else if (today.isBefore(kdtKlass?.kdtAttr?.participationStartedAt)) {
        {
          if (curSchoolMyKDTApply?.isFinished) {
            return {
              assignmentReview: {
                disabled: true,
                name: '지원완료',
                handler: () => null,
              },
            }
          } else if (!_.isEmpty(curSchoolMyKDTApply)) {
            return {
              assignmentReview: {
                disabled: true,
                name: '지원완료',
                handler: () => null,
              },
            }
          } else {
            return {
              assignmentReview: {
                disabled: true,
                name: '지원 마감',
                handler: () => null,
              },
            }
          }
        }
      }
      // 참가확인 시작
      else if (today.isBefore(kdtKlass?.startedAt)) {
        if (!prevStepPassedUser) {
          return {
            participation: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        } else if (today.isAfter(kdtKlass?.kdtAttr?.participationEndedAt)) {
          if (!curSchoolMyKDTApply?.isFinished) {
            return {
              participationReview: {
                disabled: false,
                name: '최종 참가 확인서',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                participationReview: {
                  disabled: true,
                  name: '지원 마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                participationReview: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          }
        } else {
          if (!curSchoolMyKDTApply?.isFinished) {
            return {
              participation: {
                disabled: false,
                name: '최종 참가 확인서',
                period: 'applying',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                participation: {
                  disabled: true,
                  name: '지원 마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                participation: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          }
        }
      }
      // 훈련 시작
      else {
        if (
          !_.isEmpty(
            myKDTApplyList?.filter(({ applies }) =>
              applies.some(({ status }) => _.isEqual(status, 'spare')),
            ),
          )
        ) {
          return {
            ing: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        } else if (
          myKDTApplyList &&
          myKDTApplyList[0]?.applies?.filter(el => el?.status === 'passed')?.length > 0 &&
          simplifyApplyStatus === 'ing'
        ) {
          if (curSchoolMyKDTApply?.isFinished) {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                ing: {
                  disabled: true,
                  name: '지원 마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                ing: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          } else {
            return {
              ing: {
                disabled: false,
                name: '최종 참가 확인서',
                handler: () => router.push(applyUrl),
              },
            }
          }
        } else {
          if (
            !_.isEmpty(
              myKDTApplyList?.filter(
                ({ applies }) =>
                  applies.length === 3 && applies.some(({ status }) => _.isEqual(status, 'failed')),
              ),
            )
          ) {
            return {
              ing: {
                disabled: true,
                name: '지원 마감',
                handler: () => null,
              },
            }
          } else if (
            !_.isEmpty(
              myKDTApplyList?.filter(
                ({ applies }) =>
                  applies.length === 3 &&
                  applies.every(({ status }) => _.isEqual(status, 'passed')),
              ),
            )
          ) {
            return {
              ing: {
                disabled: true,
                name: '지원 완료',
                handler: () => null,
              },
            }
          }
        }
        return {
          ing: {
            disabled: true,
            name: '지원 마감',
            handler: () => null,
          },
          end: {
            disabled: true,
            name: '지원 마감',
            handler: () => null,
          },
        }
      }
    } else {
      // 간소화 이전 1차 시작 전(사전알림기간)
      if (today.isBefore(kdtKlass?.applyStartedAt)) {
        return {
          apply: {
            disabled: false,
            name: '사전알림 신청',
            handler: () => setIsOpenPreNotice(true),
          },
        }
      }
      // mykdtId !== schoolId 인 경우
      if (!isCurCourse) {
        if (applyStatus === 'apply') {
          if (today.isBefore(kdtKlass?.applyEndedAt)) {
            return {
              apply: {
                disabled: false,
                name: '1차 지원하기',
                period: 'applying',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            return {
              apply: {
                disabled: true,
                name: '지원 마감',
                handler: () => null,
              },
            }
          }
        } else {
          return {
            [curStatus[applyStatus]]: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        }
      }
      // 1차 시작
      else if (today.isBefore(kdtKlass?.applyEndedAt)) {
        if (!curSchoolMyKDTApply?.isFinished) {
          return {
            apply: {
              disabled: false,
              name: '1차 지원하기',
              period: 'applying',
              handler: () => router.push(applyUrl),
            },
          }
        } else {
          return {
            apply: {
              disabled: true,
              name: '1차 지원 완료',
              handler: () => null,
            },
          }
        }
        // 1차 심사
      } else if (today.isBefore(kdtKlass?.kdtAttr?.assignmentStartedAt)) {
        {
          if (curSchoolMyKDTApply?.isFinished) {
            return {
              apply: {
                disabled: true,
                name: '1차 지원 완료',
                handler: () => null,
              },
            }
          } else if (!_.isEmpty(curSchoolMyKDTApply)) {
            return {
              apply: {
                disabled: true,
                name: '1차 지원 완료',
                handler: () => null,
              },
            }
          } else {
            return {
              apply: {
                disabled: true,
                name: '지원 마감',
                handler: () => null,
              },
            }
          }
        }
        // 2차 시작
      } else if (today.isBefore(kdtKlass?.kdtAttr?.participationStartedAt)) {
        if (!prevStepPassedUser) {
          return {
            assignment: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        } else if (today.isAfter(kdtKlass?.kdtAttr?.assignmentEndedAt)) {
          return {
            assignment: {
              disabled: true,
              name: '2차 제출 완료',
              handler: () => null,
            },
          }
        } else {
          if (!curSchoolMyKDTApply?.isFinished) {
            return {
              assignment: {
                disabled: false,
                name: '2차 과제 제출하기',
                period: 'applying',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            return {
              assignment: {
                disabled: true,
                name: '2차 제출 완료',
                handler: () => null,
              },
            }
          }
        }
      }
      // 3차 시작
      else if (today.isBefore(kdtKlass?.startedAt)) {
        if (!prevStepPassedUser) {
          return {
            participation: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        } else if (today.isAfter(kdtKlass?.kdtAttr?.participationEndedAt)) {
          if (!curSchoolMyKDTApply?.isFinished) {
            return {
              participation: {
                disabled: false,
                name: '최종 참가 확인서',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                participation: {
                  disabled: true,
                  name: '지원 마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                participation: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          }
        } else {
          if (!curSchoolMyKDTApply?.isFinished) {
            return {
              participation: {
                disabled: false,
                name: '최종 참가 확인서',
                period: 'applying',
                handler: () => router.push(applyUrl),
              },
            }
          } else {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                participation: {
                  disabled: true,
                  name: '지원 마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                participation: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          }
        }
      }
      // 훈련 시작
      else {
        if (
          !_.isEmpty(
            myKDTApplyList?.filter(({ applies }) =>
              applies.some(({ status }) => _.isEqual(status, 'spare')),
            ),
          )
        ) {
          return {
            ing: {
              disabled: true,
              name: '지원 마감',
              handler: () => null,
            },
          }
        } else if (
          myKDTApplyList &&
          myKDTApplyList[0]?.applies?.filter(el => el?.status === 'passed')?.length > 1
        ) {
          if (curSchoolMyKDTApply?.isFinished) {
            if (curSchoolMyKDTApply?.status === 'failed') {
              return {
                ing: {
                  disabled: true,
                  name: '지원마감',
                  handler: () => null,
                },
              }
            } else {
              return {
                ing: {
                  disabled: true,
                  name: '지원 완료',
                  handler: () => null,
                },
              }
            }
          } else {
            return {
              ing: {
                disabled: false,
                name: '최종 참가 확인서',
                handler: () => router.push(applyUrl),
              },
            }
          }
        } else {
          if (
            !_.isEmpty(
              myKDTApplyList?.filter(
                ({ applies }) =>
                  applies.length === 3 && applies.some(({ status }) => _.isEqual(status, 'failed')),
              ),
            )
          ) {
            return {
              ing: {
                disabled: true,
                name: '지원마감',
                handler: () => null,
              },
            }
          } else if (
            !_.isEmpty(
              myKDTApplyList?.filter(
                ({ applies }) =>
                  applies.length === 3 &&
                  applies.every(({ status }) => _.isEqual(status, 'passed')),
              ),
            )
          ) {
            return {
              ing: {
                disabled: true,
                name: '지원 완료',
                handler: () => null,
              },
            }
          }
        }
        return {
          ing: {
            disabled: true,
            name: '지원마감',
            handler: () => null,
          },
          end: {
            disabled: true,
            name: '지원마감',
            handler: () => null,
          },
        }
      }
    }
  }

  useEffect(() => {
    if (reviewData) setHasReview(reviewData.length !== 0)
  }, [reviewData])

  return (
    <>
      <SchoolHead course={klass} />
      {isFullPublishing ? (
        kdtKlass && (
          <FullPublishingDetail
            course={kdtKlass}
            submitType={getSubmitType(simplifyApplyStatus, fetchCurSchoolMyKDTApply, kdtKlass)}
            status={simplifyApplyStatus}
          />
        )
      ) : (
        <main ref={ref} className="min-w-[360px] pb-[350px]">
          {klass && !isKDT && (
            <CourseHero
              title={klass.title}
              subTitle={klass.subtitle as string}
              heroImg={{
                sm: klass.bannerBgImgS as string,
                md: klass.bannerBgImgM as string,
                lg: klass.bannerBgImg as string,
              }}
              textColor={klass.bannerTextColor}
            />
          )}
          {/* 모바일 modal */}
          {!_.isEmpty(klass) && applyStatus && expiryTimestamp && (
            <CourseInfo
              isMobile
              submitType={getSubmitType(
                isSimplifyKDT ? simplifyApplyStatus : applyStatus,
                fetchCurSchoolMyKDTApply,
                kdtKlass,
              )}
              status={isSimplifyKDT ? simplifyApplyStatus : applyStatus}
              course={klass}
              applyPeriodList={applyPeriodList}
              expiryTimestamp={expiryTimestamp}
              isSigned={isSigned}
              isSimplifyKDT={isSimplifyKDT as boolean}
            />
          )}
          {!isHiddenNav && (
            <KlassDetailNav
              isReview={hasReview}
              isCurriculum={!_.isEmpty(kdtKlass?.kdtAttr?.curriculum)}
              isFaq={!_.isEmpty(courseFAQList)}
            />
          )}
          {/* detail 영역 */}
          <div className="max-w-7xl lg:mt-10 lg:px-4 xl:mx-auto xl:px-6">
            <div className="lg:flex lg:flex-row lg:items-start lg:space-x-6">
              {/* 왼쪽 상세 */}
              <div>
                <div
                  id="lecture-intro"
                  style={{ maxWidth: isMobile ? '100%' : 848 }}
                  className={isEditor ? styles.container : ''}
                >
                  {klass && klass.description ? (
                    <HTMLRender html={klass.description} />
                  ) : (
                    <>
                      {klass?.descriptionObjects?.map(({ type, data }, idx) => (
                        <HTMLRender html={data as string} key={`${type}_${idx}`} />
                      ))}
                    </>
                  )}
                </div>

                <div className="mx-4 max-w-[848px] lg:mx-0">
                  {!_.isEmpty(kdtKlass?.kdtAttr.curriculum) && (
                    <div id="curriculum" className="pt-20 lg:pt-30">
                      <header className="flex flex-col lg:flex-row lg:items-end">
                        <h2
                          className="text-xl font-bold lg:text-2xl"
                          style={{ lineHeight: '30px' }}
                        >
                          커리큘럼
                        </h2>
                      </header>
                      <div className="mt-4 lg:mt-6">
                        {kdtKlass && schoolId && (
                          <KDTCurriculum
                            curriculum={kdtKlass?.kdtAttr.curriculum}
                            defaultOpenIdx={0}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {klass && hasReview && reviewData && (
                    <div id="review" className="pt-20 lg:pt-32">
                      <h2 className="text-xl font-bold lg:text-2xl">이전 기수 수강후기</h2>
                      <section className="mt-4 lg:mt-6">
                        <FullReviewBySchool reviewList={reviewData} />
                      </section>
                    </div>
                  )}
                  {!_.isEmpty(courseFAQList) && (
                    <div id="faq" className="pt-20 lg:pt-30">
                      <h2 className="text-xl font-bold lg:text-2xl">자주 묻는 질문</h2>
                      <section className="mt-8 lg:mt-6">
                        {klass && schoolId && (
                          <FAQAccordion faqList={courseFAQList} defaultOpenIdx={0} />
                        )}
                      </section>
                    </div>
                  )}
                </div>
              </div>

              {/* 오른쪽 고정되는 부분 */}
              <aside
                className="hidden h-auto shrink-0 lg:sticky lg:block"
                style={{ top: isFront ? 100 : 138, width: 360 }}
              >
                {!_.isEmpty(klass) && applyStatus && expiryTimestamp && (
                  <CourseInfo
                    submitType={getSubmitType(
                      isSimplifyKDT ? simplifyApplyStatus : applyStatus,
                      fetchCurSchoolMyKDTApply,
                      kdtKlass,
                    )}
                    status={isSimplifyKDT ? simplifyApplyStatus : applyStatus}
                    course={klass}
                    applyPeriodList={applyPeriodList}
                    expiryTimestamp={expiryTimestamp}
                    isSigned={isSigned}
                    isSimplifyKDT={isSimplifyKDT as boolean}
                  />
                )}
              </aside>
            </div>
          </div>
        </main>
      )}
      <PreNoticeDrawer
        opened={isOpenPreNotice && (isMobile ?? false)}
        onClose={() => setIsOpenPreNotice(false)}
        height="470px"
      >
        <FormProvider {...methods}>
          <PreNoticeForm onSubmit={onSubmit} defaultValues={preNoticeCourseIds}>
            <div className="px-4">
              <button className="mt-5 w-full rounded-md bg-primary p-2 font-semibold text-white">
                사전알림 신청하기
              </button>
            </div>
          </PreNoticeForm>
        </FormProvider>
      </PreNoticeDrawer>
      <FormProvider {...methods}>
        <PreNoticeFormModal
          onSubmit={onSubmit}
          opened={isOpenPreNotice && !isMobile}
          onClose={() => setIsOpenPreNotice(false)}
          subText={preNoticeInfo?.subText}
        />
      </FormProvider>
    </>
  )
}

interface IPrevStatus {
  [status: string]: string
}

const prevStatus: IPrevStatus = {
  assignment: 'apply',
  participation: 'assignment',
  participationReview: 'assignment',
  ing: 'participation',
}

export default KlassDetail
